import { useEffect, useRef, useState } from 'react';

import { useHistory, useLocation } from '@shared/core';
import { useQueryParamHelper } from '@shared/core/queryString';
import type { GeocodeData, GeocodeInput } from '@shared/core/places';
import { readRawPickupLocationParams } from './useCarSearchParamsState';
import { isValidCoordinates, useGeocodeLazy } from '@shared/core/places';
import { CAR_QUERY_PARAM_KEYS, HOTEL_LOCATION_QUERY_PARAM_KEYS } from '../CarRentalConsole.constants';

type RawPickupLocationParams = ReturnType<typeof readRawPickupLocationParams>;
type GeocodeAnchor = Readonly<{
  value: string;
  type: 'placeId' | 'destination';
}>;

const parseCoordinate = (value: string | undefined): number | undefined => {
  if (!value?.trim()) return undefined;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : undefined;
};

const hasValidRawCoordinates = ({ lat, lng }: RawPickupLocationParams): boolean =>
  isValidCoordinates({
    lat: parseCoordinate(lat),
    lng: parseCoordinate(lng),
  });

const buildAnchorKey = ({ placeId, destination }: RawPickupLocationParams, locationKey: string | undefined): string =>
  JSON.stringify([placeId ?? null, destination ?? null, locationKey ?? null]);

const hasValidGeocodeResult = (result: GeocodeData | null): result is GeocodeData => !!result && isValidCoordinates(result.location);

export const useGeocodeCarSearchLocation = () => {
  const history = useHistory();
  const location = useLocation();
  const queryParamHelper = useQueryParamHelper();

  const [geocode] = useGeocodeLazy();
  const [activeAnchorKey, setActiveAnchorKey] = useState<string | null>(null);

  const attemptedAnchorKeyRef = useRef<string | null>(null);

  const rawParams = readRawPickupLocationParams(queryParamHelper);
  const anchorKey = buildAnchorKey(rawParams, location.key);
  const hasValidCoordinates = hasValidRawCoordinates(rawParams);
  const hasGeocodeAnchor = Boolean(rawParams.placeId || rawParams.destination);
  const shouldGeocode = !hasValidCoordinates && hasGeocodeAnchor;

  const latestContextRef = useRef({
    history,
    location,
    rawParams,
    anchorKey,
    hasValidCoordinates,
  });

  latestContextRef.current = {
    history,
    location,
    rawParams,
    anchorKey,
    hasValidCoordinates,
  };

  useEffect(() => {
    if (!shouldGeocode) {
      setActiveAnchorKey(null);
      return;
    }

    if (attemptedAnchorKeyRef.current === anchorKey) return;
    attemptedAnchorKeyRef.current = anchorKey;
    setActiveAnchorKey(anchorKey);

    let cancelled = false;

    const isFresh = (anchor: GeocodeAnchor): boolean => {
      const latest = latestContextRef.current;
      if (latest.hasValidCoordinates || latest.anchorKey !== anchorKey) return false;
      return anchor.type === 'placeId' ? latest.rawParams.placeId === anchor.value : latest.rawParams.destination === anchor.value;
    };

    const writeCoordinates = (result: GeocodeData, anchor: GeocodeAnchor): boolean => {
      if (!isFresh(anchor)) return false;

      const latest = latestContextRef.current;
      const params = new URLSearchParams(latest.location.search);
      params.delete(CAR_QUERY_PARAM_KEYS.pickupLat);
      params.delete(CAR_QUERY_PARAM_KEYS.pickupLng);
      params.set(HOTEL_LOCATION_QUERY_PARAM_KEYS.lat, String(result.location.lat));
      params.set(HOTEL_LOCATION_QUERY_PARAM_KEYS.lng, String(result.location.lng));
      latest.history.replace({
        search: params.toString(),
        hash: latest.location.hash,
        pathname: latest.location.pathname,
      });
      return true;
    };

    const run = async () => {
      const attempts: Array<Readonly<{ anchor: GeocodeAnchor; input: GeocodeInput }>> = [];

      if (rawParams.placeId) {
        attempts.push({
          input: { placeId: rawParams.placeId },
          anchor: { type: 'placeId', value: rawParams.placeId },
        });
      }
      if (rawParams.destination) {
        attempts.push({
          input: { address: rawParams.destination },
          anchor: { type: 'destination', value: rawParams.destination },
        });
      }

      for (const { anchor, input } of attempts) {
        if (cancelled || !isFresh(anchor)) return;

        const result = await geocode(input);
        if (cancelled) return;

        if (hasValidGeocodeResult(result)) {
          writeCoordinates(result, anchor);
          return;
        }
      }

      const latest = latestContextRef.current;
      if (!cancelled && latest.anchorKey === anchorKey && !latest.hasValidCoordinates) {
        setActiveAnchorKey(currentKey => (currentKey === anchorKey ? null : currentKey));
      }
    };

    run();

    return () => {
      cancelled = true;
    };
  }, [anchorKey, geocode, rawParams.destination, rawParams.placeId, shouldGeocode]);

  const isGeocoding = shouldGeocode && (activeAnchorKey === anchorKey || attemptedAnchorKeyRef.current !== anchorKey);

  return {
    isGeocoding,
    pendingDestination: shouldGeocode ? rawParams.destination : undefined,
  };
};
